import { ValidationConfig } from 'components/src/inputField/types';
import { isNumber } from './utils';

const ICELANDIC_NATIONAL_ID_PATTERN = /^(\d{6}-?\d{4})$/;
const UNIVERSAL_PHONE_PATTERN = /^(\+\d{1,3}\s?)?[\d\s\-\(\)]{7,15}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const icelandicValidations = {
  firstName: {
    required: true,
    minLength: 2,
    maxLength: 50,
    custom: [
      {
        test: (value: string) => /^[a-zA-ZáéíóúýþæðöÁÉÍÓÚÝÞÆÐÖ\s\-\']+$/.test(value),
        message: 'Nafn má aðeins innihalda bókstafi',
      },
    ],
  } as ValidationConfig,

  lastName: {
    required: false,
    minLength: 2,
    maxLength: 50,
    custom: [
      {
        test: (value: string) => !value || /^[a-zA-ZáéíóúýþæðöÁÉÍÓÚÝÞÆÐÖ\s\-\']+$/.test(value),
        message: 'Eftirnafn má aðeins innihalda bókstafi',
      },
    ],
  } as ValidationConfig,

  nationalId: {
    required: true,
    pattern: ICELANDIC_NATIONAL_ID_PATTERN,
    minLength: 10,
    maxLength: 10,
    custom: [
      {
        test: (value: string) => {
          return ICELANDIC_NATIONAL_ID_PATTERN.test(value);
        },
        message: 'Kennitala ekki gild',
      },
    ],
  } as ValidationConfig,

  email: {
    required: true,
    pattern: EMAIL_PATTERN,
    custom: [
      {
        test: (value: string) => EMAIL_PATTERN.test(value),
        message: 'Vinsamlegast sláðu inn gilt netfang',
      },
    ],
  } as ValidationConfig,

  phoneNumber: {
    required: true,
    pattern: UNIVERSAL_PHONE_PATTERN,
    custom: [
      {
        test: (value: string) => UNIVERSAL_PHONE_PATTERN.test(value),
        message: 'Vinsamlegast sláðu inn gilt símanúmer',
      },
    ],
  } as ValidationConfig,

  postalCode: {
    required: true,
    custom: [
      {
        test: (value: string) => {
          const postalCodeMatch = value.match(/\b\d{3}\b/);
          const placeNameMatch = value.match(/[a-zA-ZáéíóúýþæðöÁÉÍÓÚÝÞÆÐÖ]+/);

          if (!postalCodeMatch || !placeNameMatch) return false;

          const code = parseInt(postalCodeMatch[0]);
          return code >= 100 && code <= 999;
        },
        message: 'Póstnúmer ekki gilt (101 Reykjavík eða Reykjavík 101)',
      },
    ],
  } as ValidationConfig,

  address: {
    required: true,
    minLength: 5,
    maxLength: 100,
    custom: [
      {
        test: (value: string) => /^[a-zA-ZáéíóúýþæðöÁÉÍÓÚÝÞÆÐÖ0-9\s\-\.\,]+$/.test(value),
        message: 'Heimilisfang ekki gilt',
      },
    ],
  } as ValidationConfig,

  delivery: {
    required: true,
    minLength: 1,
    custom: [
      {
        test: (value: string) => ['pickup', 'delivery', 'post'].includes(value.toLowerCase()),
        message: 'Vinsamlegast veldu gilda afhendingarmáta',
      },
    ],
  } as ValidationConfig,
} as const;

export const validateWithConfig = (
  value: string,
  config: ValidationConfig
): { isValid: boolean; errorMessage?: string } => {
  if (config.required && (!value || value.trim() === '')) {
    return { isValid: false, errorMessage: 'Þessi reitur er nauðsynlegur' };
  }

  if (!value && !config.required) {
    return { isValid: true };
  }

  const trimmedValue = value.trim();

  if (config.minLength && trimmedValue.length < config.minLength) {
    return { isValid: false, errorMessage: `Lágmarkslengd er ${config.minLength} stafir` };
  }

  if (config.maxLength && trimmedValue.length > config.maxLength) {
    return { isValid: false, errorMessage: `Hámarkslengd er ${config.maxLength} stafir` };
  }

  if (config.custom) {
    for (const customRule of config.custom) {
      if (!customRule.test(trimmedValue)) {
        return { isValid: false, errorMessage: customRule.message };
      }
    }
  }

  return { isValid: true };
};

export const validateNationalId = (nationalId: string): boolean => {
  return validateWithConfig(nationalId, icelandicValidations.nationalId).isValid;
};

export const validateEmail = (email: string): boolean => {
  return validateWithConfig(email, icelandicValidations.email).isValid;
};

export const validatePhoneNumber = (phone: string): boolean => {
  return validateWithConfig(phone, icelandicValidations.phoneNumber).isValid;
};

export const validatePostalCode = (postalCode: string): boolean => {
  return validateWithConfig(postalCode, icelandicValidations.postalCode).isValid;
};

export const validateFirstName = (name: string): boolean => {
  return validateWithConfig(name, icelandicValidations.firstName).isValid;
};

export const validateLastName = (name: string): boolean => {
  return validateWithConfig(name, icelandicValidations.lastName).isValid;
};

export const validateAddress = (address: string): boolean => {
  return validateWithConfig(address, icelandicValidations.address).isValid;
};

export const validateDelivery = (delivery: string): boolean => {
  return validateWithConfig(delivery, icelandicValidations.delivery).isValid;
};

// Enhanced validation functions that return detailed results
export const validateNationalIdWithMessage = (nationalId: string) => {
  return validateWithConfig(nationalId, icelandicValidations.nationalId);
};

export const validateEmailWithMessage = (email: string) => {
  return validateWithConfig(email, icelandicValidations.email);
};

export const validatePhoneNumberWithMessage = (phone: string) => {
  return validateWithConfig(phone, icelandicValidations.phoneNumber);
};

export const validatePostalCodeWithMessage = (postalCode: string) => {
  return validateWithConfig(postalCode, icelandicValidations.postalCode);
};

export const validateFirstNameWithMessage = (name: string) => {
  return validateWithConfig(name, icelandicValidations.firstName);
};

export const validateLastNameWithMessage = (name: string) => {
  return validateWithConfig(name, icelandicValidations.lastName);
};

export const validateAddressWithMessage = (address: string) => {
  return validateWithConfig(address, icelandicValidations.address);
};

export const validateDeliveryWithMessage = (delivery: string) => {
  return validateWithConfig(delivery, icelandicValidations.delivery);
};

// Credit Card Validation Functions

export const softValidateExpirationDate = (value: string): string | undefined => {
  const regex = /^(0[1-9]?|1[0-2]?|[2-9])(\/(\d{0,2})?)?$/;
  const result = regex.exec(value);
  if (!result) {
    return 'Gildistími þarf að vera á forminu MM/YY';
  }
  return undefined;
};

export const validateExpirationDate = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const parts = value.split('/');
  if (parts.length !== 2) {
    return 'Gildistími þarf að vera á forminu MM/YY';
  }

  const [month, year] = parts;
  const monthNum = parseInt(month, 10);
  const yearNum = parseInt(year, 10);

  if (month.length !== 2 || year.length !== 2) {
    return 'Gildistími þarf að vera á forminu MM/YY';
  }

  if (monthNum < 1 || monthNum > 12) {
    return 'Mánuður þarf að vera á milli 01 og 12';
  }

  const currentYear = new Date().getFullYear() % 100;
  const currentMonth = new Date().getMonth() + 1;

  if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
    return 'Kortið er útrunnið';
  }

  return undefined;
};

// Validates card number using Luhn algorithm
export const validateCardNumber = (number: string): boolean => {
  if (number.length < 13 || number.length > 19) return false;

  let sum = 0;
  let isEven = false;

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number.charAt(i), 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

export const formatExpirationInput = (value: string): string => {
  // Remove any non-numeric characters except /
  const cleanValue = value.replace(/[^\d/]/g, '');

  // Prevent invalid input patterns
  const parts = cleanValue.split('/');
  if (parts.length > 2 || (parts[0] && !isNumber(parts[0])) || (parts[1] && !isNumber(parts[1]))) {
    return value; // Return original if invalid
  }

  // Auto-add slash after 2 digits
  if (isNumber(cleanValue) && cleanValue.length === 2) {
    return cleanValue + '/';
  }

  // Limit total length
  if (cleanValue.length > 5) {
    return value; // Return original if too long
  }

  return cleanValue;
};

export const formatCardNumberInput = (value: string): string => {
  // Remove spaces and non-numeric characters
  return value.replace(/\D/g, '');
};

export const formatCvcInput = (value: string): string => {
  return value.replace(/\D/g, '');
};
